import type { TemplateVariables } from '../types/email.types';
import type { FlowSummary, FlowTrigger } from '@/lib/api/email-flows/types';
import { EXCLUDED_VARIABLES, TEMPLATE_TYPES } from '../constants/emailTemplates.constants';

/**
 * Determine template type based on flow and email position
 *
 * @param flow - The flow object
 * @param emailCount - Total number of emails in the flow
 * @param isFirstEmail - Whether this is the first email
 * @returns Template type string for API
 */
export function getTemplateType(flow: FlowSummary, emailCount: number, isFirstEmail: boolean): string {
    const trigger = flow.trigger || flow.trigger_type;

    // First email in time-based flow
    if (trigger === 'time' && isFirstEmail) {
        return TEMPLATE_TYPES.TIME_FIRST;
    }

    // First email in cart abandoned flow
    if (trigger === 'cart-abandonment' && isFirstEmail) {
        return TEMPLATE_TYPES.CART_ABANDONED_FIRST;
    }

    // First email in checkout flow
    if (trigger === 'checkout' && isFirstEmail) {
        return TEMPLATE_TYPES.CHECKOUT_FIRST;
    }

    // Default template for subsequent emails
    return TEMPLATE_TYPES.DEFAULT;
}

/**
 * Filter variables based on trigger type
 * Time-based flows exclude product_name variable
 *
 * @param variables - The template variables
 * @param triggerType - The flow trigger type
 * @returns Filtered template variables
 */
export function filterVariablesForTrigger(variables: TemplateVariables, triggerType: FlowTrigger): TemplateVariables {
    // For time-based flows, exclude product_name variable
    if (triggerType === 'time') {
        const excludedVars = EXCLUDED_VARIABLES.TIME_TRIGGER as readonly string[];
        return {
            subject: variables.subject.filter(v => !excludedVars.includes(v.id)),
            body: variables.body.filter(v => !excludedVars.includes(v.id)),
        };
    }

    // Return all variables for other trigger types
    return variables;
}
